// _light-instance.js — ASK-088 slice-3a · the runtime luminaire light-instance model.
//
// Turns a fixture's TOP-LEVEL catalogue lights[] (the ASK-088 typed-emitter contract,
// INTERFACE 2026-07-17: {name, position (LOCAL to Body), type:"point"|"spot", color,
// intensity, range?, angle?, penumbra?, aim?, extras}) + the fixture instance's per-instance
// knob overrides into resolved light SPECS. The stage materialises each spec into a THREE
// light parented to the fixture clone, so it sits at the authored emitter pose for free and
// travels with the fixture on a move (the clone is adopted into the per-anchor group).
//
// Per ASK-088 contract 1 the emitter POSE is authoring-owned (never a knob) — only the
// merchant-tunable channels (intensity/colour/range, +castShadow, + spot cone later) override.
// Overrides ride the per-instance knobs at `knobs.lights[<LIGHT_NAME>]` — mirroring the
// catalogue lights[] shape keyed by name, so it is multi-light-safe (contract 3: per-light
// params ride the instance row's nested knobs; delete auto-cleans them). Today all 7 shipped
// kinds emit exactly one `type:"point"` light; the spot channels resolve but stay unused until
// a spot kind ships (spot-hidden-until-real).
//
// Pure (no THREE/DOM) so it unit-tests headlessly — same discipline as _socket-transform.js.

const HEX6 = /^#?[0-9a-fA-F]{6}$/;
const MAX_INTENSITY = 50;   // mirrors the verify-luminaire-lights emit-gate guardrail (intensity ≤ 50)

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const firstFinite = (...xs) => {
	for (const x of xs) if (typeof x === "number" && Number.isFinite(x)) return x;
	return null;
};

/** Normalise a colour to "#rrggbb" (accepts "rrggbb" or "#rrggbb"); null if not a 6-hex string. */
export function normaliseHex(c) {
	if (typeof c !== "string" || !HEX6.test(c)) return null;
	const h = c[0] === "#" ? c.slice(1) : c;
	return `#${h.toLowerCase()}`;
}

/** Clamp a would-be intensity into [0, MAX_INTENSITY]; null when not a finite number (fall through). */
function clampIntensity(v) {
	if (typeof v !== "number" || !Number.isFinite(v)) return null;
	return Math.max(0, Math.min(MAX_INTENSITY, v));
}

/** A positive finite number, else null (a range must be > 0; null → THREE "no distance cutoff"). */
function positiveOrNull(v) {
	return (typeof v === "number" && Number.isFinite(v) && v > 0) ? v : null;
}

/** Merge ONE catalogue light with its per-instance knob override → a resolved spec.
 *  Only the tunable channels override; POSE (position/type/aim) is authoring-owned and passes
 *  through untouched (contract 1). Unknown / blank / out-of-range overrides fall through to the
 *  authored catalogue value — a bad knob never crashes a scene, it just doesn't apply. */
export function resolveLightParams(catalogueLight, override) {
	const l = catalogueLight || {};
	const o = override || {};
	const type = l.type === "spot" ? "spot" : "point";
	const overInt = clampIntensity(o.intensity);
	const overRange = positiveOrNull(o.range);
	const spec = {
		name: typeof l.name === "string" ? l.name : "LIGHT_1",
		type,
		// POSE — authoring-owned, copied verbatim (never overridable per contract 1).
		position: { x: num(l.position?.x), y: num(l.position?.y), z: num(l.position?.z) },
		color: normaliseHex(o.color) || normaliseHex(l.color) || "#ffffff",
		intensity: overInt != null ? overInt : (clampIntensity(l.intensity) ?? 1),
		range: overRange != null ? overRange : positiveOrNull(l.range),   // null → no distance cutoff
		// castShadow defaults OFF (tier-lean, Model-B baseline per Coord guidance 2); a knob opts in.
		castShadow: o.castShadow === true,
	};
	if (type === "spot") {
		// Spot cone is schema-ready but the controls stay HIDDEN until a spot kind ships
		// (spot-hidden-until-real). Resolve them anyway so a spot renders correctly the day one lands.
		spec.angle = firstFinite(o.angle, l.angle) ?? Math.PI / 6;
		spec.penumbra = firstFinite(o.penumbra, l.penumbra) ?? 0;
		spec.aim = l.aim && Number.isFinite(l.aim.x)
			? { x: num(l.aim.x), y: num(l.aim.y), z: num(l.aim.z) } : null;
	}
	return spec;
}

/** Resolved light specs for one fixture instance (anchor). [] when the kind emits no lights.
 *  Overrides come from `anchor.extras.lights[<name>]` (= the instance row's nested knobs). */
export function lightsForAnchor(anchor, fixtureCatalogue) {
	const kind = anchor?.fixtureKind;
	const list = kind && fixtureCatalogue?.[kind]?.lights;
	if (!Array.isArray(list) || !list.length) return [];
	const overrides = anchor?.extras?.lights || {};
	return list.map((l) => resolveLightParams(l, overrides[l?.name]));
}

/** Does this anchor's fixture kind carry emitters? (drives the luminaire render branch per anchor) */
export function anchorHasLights(anchor, fixtureCatalogue) {
	const kind = anchor?.fixtureKind;
	const list = kind && fixtureCatalogue?.[kind]?.lights;
	return Array.isArray(list) && list.length > 0;
}
